import React from 'react';
import { useTranslation } from 'localization';
import { ChannelItem, ChannelItemType, ItemSyndicationInfo } from 'types/channel';
import { getLocalizedText } from '../../../../utils/getLocalizedText';
import { usePublisherName } from '../../../../hooks/usePublisherName';
import ButtonAtom from '../../../atoms/Button/ButtonAtom';
import './ItemDetailTabMolecule.scss';

const ITEM_TYPE_KEYS: Record<ChannelItemType, string> = {
  approach: 'marketplace.common.itemType.approach',
  surface_template: 'marketplace.common.itemType.surfaceTemplate',
  assistant: 'marketplace.common.itemType.assistant',
  video: 'marketplace.common.itemType.video',
  form: 'marketplace.common.itemType.form',
  metaspace: 'marketplace.common.itemType.metaspace',
  schema: 'marketplace.common.itemType.schema',
  context: 'marketplace.common.itemType.context',
  arc_template: 'marketplace.common.itemType.arcTemplate',
  orbit_template: 'marketplace.common.itemType.orbitTemplate',
  aspect: 'marketplace.common.itemType.aspect',
  aspect_group: 'marketplace.common.itemType.aspectGroup',
  composition: 'marketplace.common.itemType.composition',
  archetype: 'marketplace.common.itemType.archetype',
};

export interface ItemDetailTabMoleculeProps {
  item: ChannelItem;
  syndicationInfo: ItemSyndicationInfo | null;
  canEdit?: boolean;
  onEdit?: () => void;
  className?: string;
  /** Heading level of the item name: 2 when the host page already owns the h1, 3 inside a dialog. */
  headingLevel?: 2 | 3;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

const ItemDetailTabMolecule: React.FC<ItemDetailTabMoleculeProps> = ({
  item,
  syndicationInfo,
  canEdit = true,
  onEdit,
  className = '',
  headingLevel = 3,
}) => {
  const { t } = useTranslation();
  const { publisherName } = usePublisherName(item.published_by);

  const HeadingTag = headingLevel === 2 ? 'h2' : 'h3';
  const classes = ['marketplace-item-detail-tab-molecule', className].filter(Boolean).join(' ');

  const displayName =
    getLocalizedText(item.item_metadata?.name) || item.item_system_name;
  const displayDescription = getLocalizedText(item.item_metadata?.description);
  const tags = item.item_metadata?.tags ?? [];

  const isSyndicated = syndicationInfo?.isSyndicated === true;
  const showEdit = canEdit && !isSyndicated && typeof onEdit === 'function';

  const versionModeLabel = (() => {
    if (syndicationInfo?.versionMode === 'pinned' && syndicationInfo.pinnedVersion != null) {
      return t('marketplace.itemDetail.details.versionModePinned', {
        version: syndicationInfo.pinnedVersion,
      });
    }
    if (syndicationInfo?.versionMode === 'latest') return t('marketplace.itemDetail.details.versionModeLatest');
    return null;
  })();

  return (
    <div className={classes}>
      <header className="marketplace-item-detail-tab-molecule__header">
        <HeadingTag className="marketplace-item-detail-tab-molecule__name">{displayName}</HeadingTag>
        {showEdit && (
          <ButtonAtom
            variant="secondary"
            shape="pill"
            size="small"
            icon="pi pi-pencil"
            label={t('common.actions.edit')}
            onClick={onEdit}
            data-testid="item-detail-edit-button"
          />
        )}
      </header>

      <dl className="marketplace-item-detail-tab-molecule__fields">
        <div className="marketplace-item-detail-tab-molecule__field">
          <dt>{t('marketplace.itemDetail.details.type')}</dt>
          <dd>{t(ITEM_TYPE_KEYS[item.item_type])}</dd>
        </div>

        {item.item_version != null && (
          <div className="marketplace-item-detail-tab-molecule__field">
            <dt>{t('marketplace.itemDetail.details.version')}</dt>
            <dd>
              {t('marketplace.common.version', { version: item.item_version })}
            </dd>
          </div>
        )}

        <div className="marketplace-item-detail-tab-molecule__field">
          <dt>{t('marketplace.itemDetail.details.publishedBy')}</dt>
          <dd>{publisherName || item.published_by}</dd>
        </div>

        <div className="marketplace-item-detail-tab-molecule__field">
          <dt>{t('marketplace.itemDetail.details.publishedAt')}</dt>
          <dd>{formatDate(item.published_at)}</dd>
        </div>

        {displayDescription && (
          <div className="marketplace-item-detail-tab-molecule__field marketplace-item-detail-tab-molecule__field--block">
            <dt>{t('marketplace.itemDetail.details.description')}</dt>
            <dd>{displayDescription}</dd>
          </div>
        )}

        {tags.length > 0 && (
          <div className="marketplace-item-detail-tab-molecule__field marketplace-item-detail-tab-molecule__field--block">
            <dt>{t('marketplace.itemDetail.details.tags')}</dt>
            <dd>
              <ul className="marketplace-item-detail-tab-molecule__tags">
                {tags.map((tag) => (
                  <li key={tag} className="marketplace-item-detail-tab-molecule__tag">
                    {tag}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        )}
      </dl>

      {isSyndicated && syndicationInfo && (
        <section
          className="marketplace-item-detail-tab-molecule__syndication"
          data-testid="item-detail-syndication-source"
        >
          <h4 className="marketplace-item-detail-tab-molecule__syndication-title">
            <i className="pi pi-share-alt" aria-hidden="true" />
            {t('marketplace.itemDetail.details.syndicationSource')}
          </h4>

          {syndicationInfo.sourceChannelSystemName && (
            <p className="marketplace-item-detail-tab-molecule__syndication-source">
              {syndicationInfo.sourceChannelSystemName}
            </p>
          )}

          {versionModeLabel && (
            <div className="marketplace-item-detail-tab-molecule__syndication-row">
              <span className="marketplace-item-detail-tab-molecule__syndication-label">
                {t('marketplace.itemDetail.details.versionMode')}
              </span>
              <span className="marketplace-item-detail-tab-molecule__syndication-value">
                {versionModeLabel}
              </span>
            </div>
          )}

          {syndicationInfo.resolvedVersion != null && (
            <div className="marketplace-item-detail-tab-molecule__syndication-row">
              <span className="marketplace-item-detail-tab-molecule__syndication-value">
                {t('marketplace.itemDetail.details.resolvedVersion', {
                  version: syndicationInfo.resolvedVersion,
                })}
              </span>
            </div>
          )}

          {syndicationInfo.lastSyncedAt && (
            <div className="marketplace-item-detail-tab-molecule__syndication-row">
              <span className="marketplace-item-detail-tab-molecule__syndication-value">
                {t('marketplace.itemDetail.details.lastSynced', {
                  date: formatDate(syndicationInfo.lastSyncedAt),
                })}
              </span>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default ItemDetailTabMolecule;
